import config from 'config';
import classNames from 'classnames';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';

import { MdArrowBack } from 'react-icons/md';

import * as toast from 'utils/toast';
import { handleError } from 'utils/errorHandler';
import {
  useGlobalRatingsQuery,
  useUpdateUserSelectedSkillsMutation,
  useUserSelectedSkillsQuery,
} from 'hooks/query/useSkillQueries';

import * as userService from 'services/user';
import * as skillService from 'services/skillService';

import Modal from 'components/common/modal';
import Empty from 'components/common/empty/Empty';
import ProfileHeader from '../components/profileHeader/ProfileHeader';
import Loading from 'components/common/loading/Loading';
import SkillRatingInfo from '../../components/SkillRatingInfo';
import { SkillPicker } from '../../components/CreateUpdateEmployeeForm/components';

import { REQUEST_NEW_SKILLS } from 'constants/skills';

import { RouteComponentProps } from 'react-router-dom';
import { EmployeeLike } from 'interface/userUtils';
import { SkillNode } from 'interface/skillTree';

interface EmployeeSkillsRouteParams {
  id?: string;
}

interface EmployeeSkillsProps extends Partial<RouteComponentProps<EmployeeSkillsRouteParams>> {
  user: { id: number; firstName: string; middleName?: string | null; lastName: string };
  history: { goBack: () => void };
  match: { params: { id?: string } };
}

interface SkillPickerEmployee {
  id: number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
}

const EmployeeSkills: React.FC<EmployeeSkillsProps> = (props) => {
  const [skills, setSkills] = useState<SkillNode[]>([]);
  const [employee, setEmployee] = useState<EmployeeLike>({});
  const [selectedSkillsDraft, setSelectedSkillsDraft] = useState<{
    sourceKey: string;
    value: SkillNode[];
  }>({ sourceKey: '', value: [] });
  const [submitting, setSubmitting] = useState(false);
  const [isSkillTreeLoading, setSkillsLoading] = useState(true);
  const [isEmployeeLoading, setEmployeeLoading] = useState(true);

  // Router hands us a fresh `match.params` object whenever the route re-renders, so depend on the id string
  // only — otherwise every Redux update from the layout retriggers fetch and duplicates network calls.
  const routeEmployeeId = props.match?.params?.id;

  const fetchEmployee = useCallback(async () => {
    if (!routeEmployeeId) {
      setEmployee({});
      setEmployeeLoading(false);
      return;
    }

    setEmployeeLoading(true);
    try {
      const employee = (await userService.fetchById(routeEmployeeId)) as EmployeeLike;

      setEmployee(employee);
    } catch (error) {
      handleError(error);
    } finally {
      setEmployeeLoading(false);
    }
  }, [routeEmployeeId]);

  const employeeId = employee.id;
  const {
    data: globalRatings = [],
    isLoading: isLoadingGlobalRatings,
    isError: isGlobalRatingsError,
    error: globalRatingsError,
    errorUpdatedAt: globalRatingsErrorUpdatedAt,
  } = useGlobalRatingsQuery();
  const {
    data: fetchedUserSkills = [],
    isLoading: isFetchingUserSkills,
    isError: isSelectedSkillsError,
    error: selectedSkillsError,
    errorUpdatedAt: selectedSkillsErrorUpdatedAt,
  } = useUserSelectedSkillsQuery(employeeId);
  const updateUserSelectedSkillsMutation = useUpdateUserSelectedSkillsMutation();
  const lastHandledGlobalRatingsErrorAtRef = useRef(0);
  const lastHandledSelectedSkillsErrorAtRef = useRef(0);

  const userSkills = useMemo<SkillNode[]>(
    () =>
      fetchedUserSkills.map((skill) => ({
        id: skill.id,
        rating: skill.rating,
        skillStatus: skill.skillStatus,
        subSkills: [],
      })),
    [fetchedUserSkills]
  );
  const userSkillsSourceKey = useMemo(
    () => userSkills.map((skill) => `${skill.id}:${skill.rating}:${skill.skillStatus ?? ''}`).join('|'),
    [userSkills]
  );
  const selectedSkills = selectedSkillsDraft.sourceKey === userSkillsSourceKey ? selectedSkillsDraft.value : userSkills;

  const fetchSkillsV2 = useCallback(async () => {
    try {
      setSkillsLoading(true);

      const { data } = (await skillService.fetchAll({})) as { data: SkillNode[] };

      setSkills(Array.isArray(data) ? data : []);
    } catch (error) {
      handleError(error);
    } finally {
      setSkillsLoading(false);
    }
  }, []);

  useEffect(() => {
    void Promise.resolve().then(fetchSkillsV2);
  }, [fetchSkillsV2]);

  useEffect(() => {
    void Promise.resolve().then(fetchEmployee);
  }, [fetchEmployee]);

  useEffect(() => {
    if (
      !isGlobalRatingsError ||
      !globalRatingsError ||
      globalRatingsErrorUpdatedAt <= lastHandledGlobalRatingsErrorAtRef.current
    ) {
      return;
    }

    handleError(globalRatingsError);
    lastHandledGlobalRatingsErrorAtRef.current = globalRatingsErrorUpdatedAt;
  }, [isGlobalRatingsError, globalRatingsError, globalRatingsErrorUpdatedAt]);

  useEffect(() => {
    if (
      !isSelectedSkillsError ||
      !selectedSkillsError ||
      selectedSkillsErrorUpdatedAt <= lastHandledSelectedSkillsErrorAtRef.current
    ) {
      return;
    }

    handleError(selectedSkillsError);
    lastHandledSelectedSkillsErrorAtRef.current = selectedSkillsErrorUpdatedAt;
  }, [isSelectedSkillsError, selectedSkillsError, selectedSkillsErrorUpdatedAt]);

  const isSkillsLoading = isSkillTreeLoading || isEmployeeLoading || isLoadingGlobalRatings || isFetchingUserSkills;

  const isUpdated = useMemo(() => {
    const mappedUserSkill = userSkills.reduce<Record<number | string, number>>((acc, curr) => {
      acc[curr.id] = curr.rating;

      return acc;
    }, {});

    const haveSameSkills =
      userSkills.length === selectedSkills.length &&
      selectedSkills.every(
        (skill) =>
          Object.prototype.hasOwnProperty.call(mappedUserSkill, skill.id) && skill.rating === mappedUserSkill[skill.id]
      );

    return !haveSameSkills;
  }, [selectedSkills, userSkills]);

  const handleUpdate = () => {
    setSubmitting(true);

    const filteredSkills = selectedSkills.map((skill) => ({ id: skill.id, rating: skill.rating }));

    updateUserSelectedSkillsMutation.mutate(
      {
        employeeId: employee.id,
        skills: filteredSkills,
      },
      {
        onSuccess: () => {
          setSelectedSkillsDraft({
            sourceKey: userSkillsSourceKey,
            value: selectedSkills,
          });

          toast.success({
            title: 'Success',
            message: 'Skill set updated successfully.',
          });
        },
        onSettled: () => {
          setSubmitting(false);
        },
      }
    );
  };

  const [showSkillRatingInfo, setShowRatingInfo] = useState(false);
  const selectedSkillNodes: SkillNode[] = selectedSkills.map((skill) => ({
    id: skill.id,
    rating: skill.rating,
    skillStatus: skill.skillStatus,
    subSkills: [],
  }));

  const { history } = props;

  const handleRequestClick = () => {
    window.open(config.newSkillRequestFormURI, '_blank');
  };

  return (
    <div>
      {isSkillsLoading || isEmployeeLoading ? (
        <div className="container loading-container">
          <Loading />
        </div>
      ) : employee.id ? (
        <div>
          <div className="title profile-wrapper">
            <ProfileHeader employee={employee as never} user={props.user} />
          </div>
          <main className="profile-body-wrapper">
            <div className="container d-flex fd-row flex-fix name-wrap">
              <div className="full-scope-card full-scope-card__header emp-info-container skills-toolbar">
                <button onClick={handleRequestClick} className="btn btn--outlined-grey btn-label f-right">
                  {REQUEST_NEW_SKILLS}
                </button>
                <button
                  onClick={() => {
                    history.goBack();
                  }}
                  disabled={submitting}
                  className="btn btn--outlined-grey btn-label f-left p-5"
                >
                  <MdArrowBack size="18" />
                  <span className="mv-5">Back</span>
                </button>
                <h3 className="employees-title skills-title">Edit Skills</h3>
              </div>
            </div>
            <Modal
              className="edit-skills-modal"
              isOpen={showSkillRatingInfo}
              closeButton={true}
              renderFooter={false}
              onClose={() => {
                setShowRatingInfo(false);
              }}
              title={{ text: 'Frequently Asked Questions' }}
              htmlContent={<SkillRatingInfo />}
            />
            <SkillPicker
              label=""
              skills={skills}
              globalRatings={globalRatings}
              employee={employee as SkillPickerEmployee}
              showAddSkills={false}
              selectedSkills={selectedSkillNodes}
              onSelectedSkillsChange={(nextSelectedSkills) => {
                setSelectedSkillsDraft({
                  sourceKey: userSkillsSourceKey,
                  value: nextSelectedSkills,
                });
              }}
            />
            <div className="edit-skill-footer">
              <div className="container d-flex fd-row flex-fix name-wrap">
                <div className="full-scope-card full-scope-card__header emp-info-container skills-toolbar">
                  <button
                    onClick={() => {
                      history.goBack();
                    }}
                    disabled={submitting}
                    className="btn btn--outlined-grey btn-label f-left p-5"
                  >
                    <span className="mv-5">Back</span>
                  </button>
                  <button
                    onClick={handleUpdate}
                    disabled={!isUpdated || submitting}
                    className={classNames('btn btn-label btn--curved f-right', {
                      'btn--primary': isUpdated,
                      'btn--disabled': !isUpdated,
                    })}
                  >
                    {submitting ? <Loading /> : 'Add Skills'}
                  </button>
                  <button
                    onClick={() => {
                      history.goBack();
                    }}
                    disabled={submitting}
                    className="btn btn--outlined-grey btn-label f-right p-5 mr-10"
                  >
                    <span className="mv-5">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      ) : (
        <main>
          <div className="container">
            <div className="full-scope-card empty-container__wrapper">
              <Empty message="Employee does not exist!" />
            </div>
          </div>
        </main>
      )}
    </div>
  );
};

export default EmployeeSkills;
